import webdriverio from './assets/images/webdriverio.svg';
import playwright from './assets/images/playwright.svg';
import puppeteer from './assets/images/puppeteer.svg';
import webdriverjs from './assets/images/webdriverjs.svg';
import seleniumwebdriver from './assets/images/seleniumwebdriver.svg';
import cypress from './assets/images/cypress.svg';
import { SettingsSchemaGeneralAccessibilityStandardValueEnum } from '@deque/orgwide-settings-client';
import { ProductSlugs } from '@deque/billing-utils';

// Keep this for backward compatibility with the old automation names [Used all over the application].
// Please extend this but dont directly use this in the new code.
export const AUTOMATIONS = {
  CYPRESS: 'Cypress',
  WEBDRIVERJS: 'WebDriverJS',
  PLAYWRIGHT: 'Playwright',
  PLAYWRIGHT_TEST: 'Playwright Test',
  PUPPETEER: 'Puppeteer',
  WEBDRIVERIO: 'WebdriverIO',
  WEBDRIVERIOTESTRUNNER: 'WebdriverIO Testrunner'
};

const JS_FRAMEWORKS = {
  ...AUTOMATIONS
};

const JAVA_FRAMEWORKS = {
  WEBDRIVER: 'Selenium WebDriver'
};

export const FRAMEWORKS = {
  javascript: JS_FRAMEWORKS,
  java: JAVA_FRAMEWORKS,
  typescript: JS_FRAMEWORKS
};

export const MobileAutomationSupportedFrameworks = {
  // We will not use Appium for the start: https://github.com/dequelabs/walnut/issues/11662
  Espresso: 'Espresso',
  XCUITest: 'XCUITest',
  UIAutomator: 'UI Automator'
};

type Automations = typeof AUTOMATIONS & typeof JAVA_FRAMEWORKS;

export type AutomationKey = keyof Automations;
export type Automation = Automations[AutomationKey];
export const AUTOMATION_IMAGES: Record<Automation, string> = {
  [JS_FRAMEWORKS.CYPRESS]: cypress,
  [JS_FRAMEWORKS.PLAYWRIGHT]: playwright,
  [JS_FRAMEWORKS.PLAYWRIGHT_TEST]: playwright,
  [JS_FRAMEWORKS.PUPPETEER]: puppeteer,
  [JS_FRAMEWORKS.WEBDRIVERIO]: webdriverio,
  [JS_FRAMEWORKS.WEBDRIVERIOTESTRUNNER]: webdriverio,
  [JS_FRAMEWORKS.WEBDRIVERJS]: webdriverjs,
  [JAVA_FRAMEWORKS.WEBDRIVER]: seleniumwebdriver
};

export interface ProjectInfo {
  automation: Automation;
  name: string;
}

export interface SeverityCounts {
  totalCurrent: number;
  totalLast: number;
  numNew: number;
  numFixed: number;
}

export type SeveritiesType = 'critical' | 'serious' | 'moderate' | 'minor';
export type AdditionalFiltersType =
  | 'new_issues'
  | 'best-practice'
  | 'experimental';

export type PageType = 'git' | 'gitless' | 'branch';
export const ACCESSIBILITY_STANDARDS: SettingsSchemaGeneralAccessibilityStandardValueEnum[] =
  Object.values(SettingsSchemaGeneralAccessibilityStandardValueEnum);

export type Impact = SeveritiesType | AdditionalFiltersType;
export type IssueFilter =
  | SeveritiesType
  | AdditionalFiltersType
  | SettingsSchemaGeneralAccessibilityStandardValueEnum;

export type Impacts = ReadonlyArray<Impact | IssueFilter>;
export const IMPACTS: Impacts = ['critical', 'serious', 'moderate', 'minor'];

export const FILTER_TAGS: IssueFilter[] = [
  ...IMPACTS,
  'new_issues',
  'best-practice',
  'experimental'
];

export enum SourceAppType {
  Integration = ProductSlugs.axeDevToolsWatcher, // Refers to the package @axe-core/watcher
  Mobile = ProductSlugs.axeDevToolsMobile,
  Api = 'api', // Refers to the axe devtools for web API's e.g. @axe-devtools-npm/playwright
  Cli = 'cli' // Refers to the package @axe-devtools-npm/cli
}

export enum AutomationSupportedLanguages {
  Javascript = 'javascript',
  Typescript = 'typescript',
  Java = 'java'
}

export const ProductDocsAutomationLanguages = {
  [AutomationSupportedLanguages.Javascript]: 'js',
  [AutomationSupportedLanguages.Typescript]: 'ts',
  [AutomationSupportedLanguages.Java]: 'java'
};

export const ProductDocsAutomationPlatforms = {
  [JS_FRAMEWORKS.CYPRESS]: 'cypress',
  [JS_FRAMEWORKS.PLAYWRIGHT]: 'playwright',
  [JS_FRAMEWORKS.PLAYWRIGHT_TEST]: 'playwright-test',
  [JS_FRAMEWORKS.PUPPETEER]: 'puppeteer',
  [JS_FRAMEWORKS.WEBDRIVERIO]: 'wdio',
  [JS_FRAMEWORKS.WEBDRIVERIOTESTRUNNER]: 'wdio-testrunner',
  [JS_FRAMEWORKS.WEBDRIVERJS]: 'wdjs',
  [JAVA_FRAMEWORKS.WEBDRIVER]: 'selenium'
};

/** FEATURE FLAGS */

export const AXE_WATCHER_PAID_PLANS_V1 = 'axe_devtools_watcher_paid_plans_v1';
export const AXE_DEVHUB_STARTUP_V3 = 'axe_devhub_startup_v3';
export const AXE_DEVHUB_STARTUP_CLI = 'axe_devhub_startup_cli';
export const AXE_DEVHUB_STARTUP_APIS = 'axe_devhub_startup_apis';
export const AXE_DEVHUB_JAVA_SELENIUM = 'axe_devhub_java_selenium';
export const AXE_DEVHUB_RESULTEMPTYSTATE = 'axe_devhub_resultemptystate';
export const AXE_DEVHUB_POLLING_OVERVIEWS_V1 =
  'axe_devhub_polling_overviews_v1';
export const AXE_DEVHUB_SINGLE_ISSUE_PAGE =
  'launchpad_devhub_single_issue_page_v1';
export const AXE_DEVHUB_SESSIONEND_BUTTON = 'axe_devhub_sessionend_button';

export type ExportFormat = 'json' | 'csv';

export const MIN_OVERVIEW_POLLING_TIMEOUT_MS = 2000;
export const MAX_OVERVIEW_POLLING_TIMEOUT_MS = 1024000; // 17 minutes
export const AXE_DEVHUB_SHOW_SESSION_END_TIMEOUT =
  'axe_devhub_sessionend_timeout';
export const AXE_DEVHUB_GLOBAL_CONFIG_V1 = 'axe_devhub_global_configs_v1';
export const AXE_DEVHUB_MOBILE_V1 = 'axe_devhub_mobile_1';
export const AXE_DEVHUB_LAUNCHPAD_ISSUES = 'axe_devhub_launchpad_issues_v1';
export const AXE_DEVHUB_MULTIUSER_PROJECTS_DECOUPLE =
  'axe_devhub_multiuser_projects_decouple';
